import _ from 'lodash';
import { useEffect, useState } from 'react';

import DiagnosisAppInstallBottomSheet from '@/components/templates/nonBenefit/home/components/DiagnosisAppInstallBottomSheet';
import NonBenefitFilterModal from '@/components/templates/nonBenefit/home/components/NonBenefitFilterModal';
import { EPixelEventName } from '@/constants/pixel';
import useGetNonBenefitDiagnosisPriceSupportInvitationCode from '@/entities/non-benefit/hooks/useGetNonBenefitDiagnosisPriceSupportInvitationCode';
import useAdBanner from '@/hooks/ad/useAdBanner';
import useSearchKoreaAddressV2 from '@/hooks/hospital/search/useSearchKoreaAddressV2';
import useNonBenefitMedicinesForSeo from '@/hooks/nonBenefit/useNonBenefitMedicinesForSeo';
import useNonBenefitPriceTotalSummary from '@/hooks/nonBenefit/useNonBenefitPriceTotalSummary';
import useSearchNonF2fDoctorsForSeoByUser from '@/hooks/nonBenefit/useSearchNonF2fDoctorsForSeoByUser';
import useGetKeywordSearchMagazineInfinite from '@/hooks/search/magazine/useGetKeywordSearchMagazineInfinite';
import useGetKeywordQuestionCardInfinite from '@/hooks/search/qna/useGetKeywordQuestionCardInfinite';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import { AdPageType } from '@/models/ad/list';
import { KoreaAddress, NonBenefitMedicine, NonBenefitNonF2fDoctor, Region } from '@/models/nonBenefit';
import { Client } from '@/types/app';
import { BreadCrumb, ROUTES } from '@/types/common';
import { SearchKoreaAddressV2Vo, SelectedRegion } from '@/types/hospital';
import { MagazineType } from '@/types/magazine';
import { MODAL_TYPE } from '@/types/modal';
import { MedicineUnit, NonBenefitHomeQueries } from '@/types/nonBenefit';
import Airbridge from '@/utils/airbridge';
import Pixel from '@/utils/pixel/pixel';
import { addQueryString } from '@/utils/url';
import { useToast } from 'doctornow-design-system';

const useNonBenefitHomeViewModel = (client: Client) => {
  const { router, moveToPage } = useCustomRouter();
  const { showModal, closeModal } = useModal();
  const queries = router.query as NonBenefitHomeQueries;
  const { treatmentMedicineUnitId = '', regionCode = '' } = queries;
  const { toast } = useToast();

  const breadCrumbs: BreadCrumb[] = [
    {
      label: '비급여 진료비',
      url: ROUTES.NON_BENEFIT.HOME.path + addQueryString(queries),
      logName: 'non_benefit_home',
    },
  ];

  const [isAddressFilterModalOpen, setIsAddressFilterModalOpen] = useState(false);

  const [isEnableQnaPagination, setIsEnableQnaPagination] = useState(true);

  const { bannerAdExchange, handleSendExposureLog } = useAdBanner(AdPageType.NonBenefitDetail, client);

  /* 비급여 목록 */
  const { nonBenefitMedicines } = useNonBenefitMedicinesForSeo();

  const { invitationCode } = useGetNonBenefitDiagnosisPriceSupportInvitationCode();

  /* 선택된 비급여 및 비급여 유닛 */
  const selectedMedicine = nonBenefitMedicines.findMedicineByTreatmentMedicineUnitId(treatmentMedicineUnitId);
  const selectedMedicineUnit = nonBenefitMedicines.findMedicineUnitByTreatmentMedicineUnitId(treatmentMedicineUnitId);

  /* 비급여 가격 정보 */
  const { nonBenefitPriceTotalSummary } = useNonBenefitPriceTotalSummary(selectedMedicine.id, regionCode);

  const selectedRegionSummary =
    nonBenefitPriceTotalSummary.getSelectedRegionSummaryByTreatmentMedicineUnitId(treatmentMedicineUnitId);

  const priceDisplayGapText = nonBenefitPriceTotalSummary.getComparePriceDisplayGapText(treatmentMedicineUnitId);

  /* 비급여 검색 결과 의사리스트 */
  const { nonBenefitDoctors } = useSearchNonF2fDoctorsForSeoByUser({
    regionCode,
    treatmentMedicineUnitId,
    limit: 3,
    page: 1,
  });

  /* 지역 검색 */
  const queryRegion = new Region(regionCode);
  const { data: siDoList = [] } = useSearchKoreaAddressV2({
    siDoCode: null,
    siGunGuCode: null,
  });
  const { data: siGunGuList = [] } = useSearchKoreaAddressV2({
    siDoCode: queryRegion.excludeSejongSiDoRegionCode(),
    siGunGuCode: null,
  });
  const { data: dongList = [] } = useSearchKoreaAddressV2({
    siDoCode: queryRegion.regionCodes.siDoCode,
    siGunGuCode: queryRegion.excludeSejongSiGunGuRegionCode(),
  });

  /* 선택된 지역 */
  const koreaAddress = new KoreaAddress(siDoList, siGunGuList, dongList);

  const selectedAddressList = koreaAddress.getSelectedRegionByDepth(queryRegion);

  const selectedAddressTitle = koreaAddress.getAddressTitle(queryRegion);
  const selectedFullAddressTitle = koreaAddress.getFullAddressTitle(queryRegion);

  /* 관련 컨텐츠 및 QNA */
  const { magazines: faqs } = useGetKeywordSearchMagazineInfinite({
    keyword: selectedMedicine.treatment.name,
    size: 7,
    contentType: MagazineType.FAQ,
  });

  const { magazines } = useGetKeywordSearchMagazineInfinite({
    keyword: selectedMedicine.treatment.name,
    size: 7,
    contentType: MagazineType.ARTICLE,
  });

  const { qnas, fetchSearchedQnaNextPage } = useGetKeywordQuestionCardInfinite({
    keyword: selectedMedicine.treatment.name,
    size: 5,
  });

  const onUpdateAddress = (item: SearchKoreaAddressV2Vo) => {
    router.replace({
      query: {
        ...queries,
        regionCode: item.regionCode,
      },
    });
  };

  const onSelectAddress = (item: SearchKoreaAddressV2Vo) => {
    onUpdateAddress(item);
    onCloseSearchAddressFilterModal();
  };

  const selectMedicine = (item: NonBenefitMedicine) => {
    router.replace({
      query: {
        ...queries,
        treatmentMedicineUnitId: item.units[0].id,
      },
    });
    closeModal();
  };
  const selectMedicineUnit = (item: MedicineUnit) => {
    router.replace({
      query: {
        ...queries,
        treatmentMedicineUnitId: item.id,
      },
    });
    closeModal();
  };

  const onCloseSearchAddressFilterModal = () => {
    setIsAddressFilterModalOpen(false);
  };
  const onOpenSearchAddressFilterModal = () => {
    setIsAddressFilterModalOpen(true);
  };

  const onOpenMedicineFilterModal = () => {
    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <NonBenefitFilterModal<NonBenefitMedicine>
            title={'진료 종류를 선택해주세요'}
            list={nonBenefitMedicines.medicines}
            onClick={selectMedicine}
            selectedItem={selectedMedicine}
            getItemName={(item) => item.medicineName}
          />
        ),
        onClickOverlay: closeModal,
      },
    });
  };
  const onOpenMedicineUnitFilterModal = () => {
    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <NonBenefitFilterModal<MedicineUnit>
            title={'처방 단위를 선택해주세요'}
            /* 처방 단위를 최대 3개까지 노출하기로 함 */
            list={selectedMedicine.units.slice(0, 3)}
            onClick={selectMedicineUnit}
            selectedItem={selectedMedicineUnit}
            getItemName={(item) => `${item.unit}${item.unitTypeDisplayName}`}
          />
        ),
        onClickOverlay: closeModal,
      },
    });
  };

  const onClickRelatedMagazine = (magazinePid: string) => {
    moveToPage({
      webLink: `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`,
      mobileScreenName: ROUTES.MAGAZINE.DETAIL.appScreenName,
      params: {
        id: magazinePid,
      },
    });
  };
  const onClickRelatedFaq = (magazinePid: string) => {
    moveToPage({
      webLink: `${ROUTES.FAQ.DETAIL.path}/${magazinePid}`,
      mobileScreenName: ROUTES.FAQ.DETAIL.appScreenName,
      params: {
        id: magazinePid,
      },
    });
  };
  const onClickRelatedQuestion = (questionPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: {
        questionId: questionPid,
      },
    });
  };

  const onClickOpenAppInstallLink = () => {
    Airbridge.setDeepLink({
      buttonID: `selected-medicine-${selectedMedicine.id}`,
      deeplinkUrl: `${selectedMedicine.deepLinkUrl}${invitationCode ? `&invitationCode=${invitationCode}` : ''}`,
      ctaParams: {
        cta_param_1: '우리동네진료가격',
        cta_param_2: selectedMedicine.medicineName,
        cta_param_3: selectedMedicine.treatment.name,
      },
    });
  };

  const onClickDoctor = (doctor: NonBenefitNonF2fDoctor, medicine: NonBenefitMedicine) => {
    router.push(
      `${ROUTES.DOCTOR.DETAIL.path}/${doctor.publicId}${addQueryString({
        type: 'treatment', // 앱은 symptom | subject 고 웹은 treatment | department 인 상태..
        categoryId: medicine.treatment.id,
        treatmentMedicineId: medicine.id,
      })}`
    );
  };

  const onClickCopyInvitationCode = () => {
    if (navigator.clipboard) {
      toast({ text: '복사가 완료되었습니다' });
      navigator.clipboard.writeText(invitationCode ?? '');
    }
  };

  const onOpenDiagnosisAppInstallBottomSheet = () => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      region: selectedAddressTitle,
      drug: selectedMedicine.medicineName,
    });

    showModal({
      type: MODAL_TYPE.BASIC,
      props: {
        body: (
          <DiagnosisAppInstallBottomSheet
            client={client}
            treatmentMedicineId={selectedMedicine.id}
            treatmentProductMedicineName={selectedMedicine.treatmentProductMedicineName}
            invitationCode={invitationCode}
            onClose={closeModal}
            qrCodeUrl={`${selectedMedicine.installQrcodeUrl}${invitationCode ? `?invitationCode=${invitationCode}` : ''}`}
            onClickOpenAppInstallLink={onClickOpenAppInstallLink}
            onClickCopyInvitationCode={onClickCopyInvitationCode}
          />
        ),
      },
    });
  };

  const onClickNextQna = _.debounce(
    () => {
      if (!isEnableQnaPagination) {
        return;
      }

      fetchSearchedQnaNextPage();
      setIsEnableQnaPagination(false);
    },
    1000,
    {
      leading: true,
      trailing: false,
    }
  );

  const getSearchAddressFilterModalProps = (): {
    siDoList: SearchKoreaAddressV2Vo[];
    siGunGuList: SearchKoreaAddressV2Vo[];
    dongList: SearchKoreaAddressV2Vo[];
    selectedRegion: SelectedRegion;
    onSelectAddress: (item: SearchKoreaAddressV2Vo) => void;
    onUpdateAddress: (item: SearchKoreaAddressV2Vo) => void;
    isOpened: boolean;
    isSejong: boolean;
    client: Client;
    switchRegionFilter: () => void;
  } => {
    return {
      switchRegionFilter: onCloseSearchAddressFilterModal,
      siDoList,
      siGunGuList,
      dongList,
      selectedRegion: selectedAddressList,
      onSelectAddress: onSelectAddress,
      onUpdateAddress: onUpdateAddress,
      isOpened: true,
      isSejong: queryRegion.isSejongRegion,
      client,
    };
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      region: selectedAddressTitle,
      drug: selectedMedicine.medicineName,
    });
  };

  useEffect(() => {
    /* 카테고리 변경시 qna 페이지네이션 재허용 */
    setIsEnableQnaPagination(true);
  }, [selectedMedicine.treatment.id]);

  useEffect(() => {
    Pixel.trackCustom(EPixelEventName.PageIn, {
      region: selectedAddressTitle,
      drug: selectedMedicine.medicineName,
    });
  }, [selectedMedicine, selectedAddressTitle]);

  return {
    bannerAdExchange,
    breadCrumbs,
    queries,
    qnas,
    faqs,
    magazines,
    isEnableQnaPagination,
    nonBenefitPriceTotalSummary,
    nonBenefitMedicines,
    koreaAddress,
    queryRegion,
    nonBenefitDoctors,
    selectedAddressList,
    selectedMedicine,
    selectedAddressTitle,
    selectedFullAddressTitle,
    selectedMedicineUnit,
    selectedRegionSummary,
    priceDisplayGapText,
    isAddressFilterModalOpen,
    onClickNextQna,
    handleSendExposureLog,
    onOpenSearchAddressFilterModal,
    onOpenMedicineFilterModal,
    onOpenMedicineUnitFilterModal,
    onClickRelatedMagazine,
    onClickRelatedFaq,
    onClickRelatedQuestion,
    onClickDoctor,
    onOpenDiagnosisAppInstallBottomSheet,
    getSearchAddressFilterModalProps,
    onClickTopCta,
  };
};

export default useNonBenefitHomeViewModel;
